import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Settings,
  AlertTriangle,
  Shield,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Save,
  DollarSign,
  Activity,
  User,
  Globe,
  Repeat,
  Clock,
  CheckCircle,
  Sliders,
  ListChecks,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIA_ICONS = {
  MONTO: DollarSign,
  PATRON: Activity,
  CLIENTE: User,
  GEOGRAFICO: Globe,
  OPERACION: Repeat,
  HISTORIAL: Clock,
  COMPORTAMIENTO: AlertTriangle,
};

const CATEGORIA_COLORS = {
  MONTO: 'bg-green-100 text-green-700',
  PATRON: 'bg-purple-100 text-purple-700',
  CLIENTE: 'bg-blue-100 text-blue-700',
  GEOGRAFICO: 'bg-amber-100 text-amber-700',
  OPERACION: 'bg-cyan-100 text-cyan-700',
  HISTORIAL: 'bg-slate-100 text-slate-700',
  COMPORTAMIENTO: 'bg-red-100 text-red-700',
};

export default function ROSScoringConfig() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('factores');
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [showFactorDialog, setShowFactorDialog] = useState(false);
  const [editingFactor, setEditingFactor] = useState(null);
  const [factorForm, setFactorForm] = useState({
    id: '',
    nombre: '',
    descripcion: '',
    categoria: 'PATRON',
    puntos_base: 20,
    activo: true,
    parametros: {}
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ros-scoring/config');
      setConfig(res.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFactor = async (factorId, updates) => {
    try {
      await api.put(`/ros-scoring/factores/${factorId}`, updates);
      toast.success('Factor actualizado');
      loadConfig();
    } catch (error) {
      toast.error('Error al actualizar factor');
    }
  };

  const handleToggleFactor = async (factorId, activo) => {
    await handleUpdateFactor(factorId, { activo });
  };

  const handleUpdatePuntos = async (factorId, puntos) => {
    const puntosNum = parseInt(puntos);
    if (isNaN(puntosNum) || puntosNum < 0) return;
    await handleUpdateFactor(factorId, { puntos_base: puntosNum });
  };

  const handleSaveFactor = async () => {
    if (!factorForm.nombre || !factorForm.id) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    
    setSaving(true);
    try {
      if (editingFactor) {
        await api.put(`/ros-scoring/factores/${editingFactor.id}`, {
          nombre: factorForm.nombre,
          descripcion: factorForm.descripcion,
          categoria: factorForm.categoria,
          puntos_base: factorForm.puntos_base,
          activo: factorForm.activo
        });
        toast.success('Factor actualizado');
      } else {
        await api.post('/ros-scoring/factores', factorForm);
        toast.success('Factor creado');
      }
      setShowFactorDialog(false);
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFactor = async (factorId) => {
    if (!window.confirm('¿Eliminar este factor de riesgo?')) return;
    
    try {
      await api.delete(`/ros-scoring/factores/${factorId}`);
      toast.success('Factor eliminado');
      loadConfig();
    } catch (error) {
      toast.error('Error al eliminar factor');
    }
  };

  const handleUpdateUmbral = async (nivel, field, value) => {
    try {
      await api.put(`/ros-scoring/umbrales/${nivel}`, { [field]: value });
      toast.success('Umbral actualizado');
      loadConfig();
    } catch (error) {
      toast.error('Error al actualizar umbral');
    }
  };

  const openEditFactor = (factor) => {
    setEditingFactor(factor);
    setFactorForm({
      id: factor.id,
      nombre: factor.nombre,
      descripcion: factor.descripcion,
      categoria: factor.categoria,
      puntos_base: factor.puntos_base,
      activo: factor.activo,
      parametros: factor.parametros || {}
    });
    setShowFactorDialog(true);
  };

  const openNewFactor = () => {
    setEditingFactor(null);
    setFactorForm({
      id: '',
      nombre: '',
      descripcion: '',
      categoria: 'PATRON',
      puntos_base: 20,
      activo: true,
      parametros: {}
    });
    setShowFactorDialog(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="animate-spin text-blue-600" size={40} />
      </div>
    );
  }

  const totalPuntosActivos = config?.factores?.filter(f => f.activo).reduce((sum, f) => sum + f.puntos_base, 0) || 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="text-purple-600" />
            Configuración de Scoring ROS
          </h1>
          <p className="text-slate-500 mt-1">
            Configure los factores de riesgo y umbrales para la evaluación de Reportes de Operaciones Sospechosas
          </p>
        </div>
        <Button onClick={loadConfig} variant="outline">
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Factores Activos</p>
                <p className="text-3xl font-bold text-purple-700">
                  {config?.factores?.filter(f => f.activo).length || 0}
                </p>
              </div>
              <Activity className="text-purple-400" size={40} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Score Máximo</p>
                <p className="text-3xl font-bold text-blue-700">{totalPuntosActivos}</p>
              </div>
              <BarChart3 className="text-blue-400" size={40} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Umbral Alto</p>
                <p className="text-3xl font-bold text-amber-700">
                  {config?.umbrales?.alto?.min || 101}+
                </p>
              </div>
              <AlertTriangle className="text-amber-400" size={40} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Umbral Crítico</p>
                <p className="text-3xl font-bold text-red-700">
                  {config?.umbrales?.critico?.min || 151}+
                </p>
              </div>
              <Shield className="text-red-400" size={40} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="factores" className="gap-2">
            <Sliders size={16} />
            Factores de Riesgo
          </TabsTrigger>
          <TabsTrigger value="umbrales" className="gap-2">
            <BarChart3 size={16} />
            Umbrales de Decisión
          </TabsTrigger>
          <TabsTrigger value="checklist" className="gap-2">
            <ListChecks size={16} />
            Checklist de Evaluación
          </TabsTrigger>
        </TabsList>

        {/* Tab: Factores */}
        <TabsContent value="factores" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Factores de Riesgo</CardTitle>
              <Button onClick={openNewFactor} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Plus size={16} className="mr-2" />
                Nuevo Factor
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Activo</TableHead>
                    <TableHead>Factor</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="w-24 text-center">Puntos</TableHead>
                    <TableHead className="w-24 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {config?.factores?.map(factor => {
                    const CatIcon = CATEGORIA_ICONS[factor.categoria] || Activity;
                    return (
                      <TableRow key={factor.id} className={!factor.activo ? 'opacity-50' : ''}>
                        <TableCell>
                          <Switch
                            checked={factor.activo}
                            onCheckedChange={(checked) => handleToggleFactor(factor.id, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{factor.nombre}</p>
                            <p className="text-sm text-slate-500 line-clamp-1">{factor.descripcion}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${CATEGORIA_COLORS[factor.categoria]} flex items-center gap-1 w-fit`}>
                            <CatIcon size={12} />
                            {factor.categoria}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            value={factor.puntos_base}
                            onChange={(e) => handleUpdatePuntos(factor.id, e.target.value)}
                            className="w-20 text-center mx-auto"
                            min={0}
                            max={100}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditFactor(factor)}
                            >
                              <Edit2 size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteFactor(factor.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Umbrales */}
        <TabsContent value="umbrales" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Umbrales de Decisión</CardTitle>
              <p className="text-sm text-slate-500">
                Configure los rangos de puntuación y las recomendaciones para cada nivel de riesgo
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Visualización de la escala */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-3">Escala de Riesgo</p>
                  <div className="flex h-8 rounded-lg overflow-hidden">
                    <div 
                      className="flex items-center justify-center text-xs font-medium text-white"
                      style={{ 
                        backgroundColor: config?.umbrales?.bajo?.color,
                        width: `${(config?.umbrales?.bajo?.max / totalPuntosActivos) * 100}%`,
                        minWidth: '60px'
                      }}
                    >
                      BAJO
                    </div>
                    <div 
                      className="flex items-center justify-center text-xs font-medium text-white"
                      style={{ 
                        backgroundColor: config?.umbrales?.medio?.color,
                        width: `${((config?.umbrales?.medio?.max - config?.umbrales?.medio?.min) / totalPuntosActivos) * 100}%`,
                        minWidth: '60px'
                      }}
                    >
                      MEDIO
                    </div>
                    <div 
                      className="flex items-center justify-center text-xs font-medium text-white"
                      style={{ 
                        backgroundColor: config?.umbrales?.alto?.color,
                        width: `${((config?.umbrales?.alto?.max - config?.umbrales?.alto?.min) / totalPuntosActivos) * 100}%`,
                        minWidth: '60px'
                      }}
                    >
                      ALTO
                    </div>
                    <div 
                      className="flex items-center justify-center text-xs font-medium text-white flex-1"
                      style={{ 
                        backgroundColor: config?.umbrales?.critico?.color,
                        minWidth: '60px'
                      }}
                    >
                      CRÍTICO
                    </div>
                  </div>
                </div>

                {/* Configuración de cada umbral */}
                <div className="grid grid-cols-2 gap-4">
                  {['bajo', 'medio', 'alto', 'critico'].map(nivel => {
                    const umbral = config?.umbrales?.[nivel] || {};
                    return (
                      <Card 
                        key={nivel} 
                        className="border-2"
                        style={{ borderColor: umbral.color }}
                      >
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: umbral.color }}
                            />
                            {nivel.charAt(0).toUpperCase() + nivel.slice(1)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Mínimo</Label>
                              <Input
                                type="number"
                                value={umbral.min || 0}
                                onChange={(e) => handleUpdateUmbral(nivel, 'min', parseInt(e.target.value))}
                                min={0}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Máximo</Label>
                              <Input
                                type="number"
                                value={umbral.max || 0}
                                onChange={(e) => handleUpdateUmbral(nivel, 'max', parseInt(e.target.value))}
                                min={0}
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Recomendación</Label>
                            <Select
                              value={umbral.recomendacion || ''}
                              onValueChange={(v) => handleUpdateUmbral(nivel, 'recomendacion', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CERRAR_ALERTA">Cerrar Alerta</SelectItem>
                                <SelectItem value="INVESTIGAR_MAS">Investigar Más</SelectItem>
                                <SelectItem value="EVALUAR_ROS">Evaluar ROS</SelectItem>
                                <SelectItem value="GENERAR_ROS">Generar ROS</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Color</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="color"
                                value={umbral.color || '#000000'}
                                onChange={(e) => handleUpdateUmbral(nivel, 'color', e.target.value)}
                                className="w-12 h-8 p-1 cursor-pointer"
                              />
                              <Input
                                type="text"
                                value={umbral.color || ''}
                                onChange={(e) => handleUpdateUmbral(nivel, 'color', e.target.value)}
                                className="flex-1"
                                placeholder="#000000"
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Checklist */}
        <TabsContent value="checklist" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Checklist de Evaluación</CardTitle>
              <p className="text-sm text-slate-500">
                Preguntas que el analista debe responder antes de tomar una decisión sobre el ROS
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {config?.checklist_evaluacion?.map((item, idx) => (
                  <div 
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{item.pregunta}</p>
                        <Badge 
                          variant="outline" 
                          className={item.obligatorio ? 'text-red-600 border-red-300' : 'text-slate-500'}
                        >
                          {item.obligatorio ? 'Obligatorio' : 'Opcional'}
                        </Badge>
                      </div>
                    </div>
                    <Switch
                      checked={item.obligatorio}
                      onCheckedChange={async (checked) => {
                        try {
                          await api.put(`/ros-scoring/checklist/${item.id}`, { obligatorio: checked });
                          toast.success('Checklist actualizado');
                          loadConfig();
                        } catch (error) {
                          toast.error('Error al actualizar');
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Factor */}
      <Dialog open={showFactorDialog} onOpenChange={setShowFactorDialog}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingFactor ? <Edit2 className="text-blue-600" /> : <Plus className="text-green-600" />}
              {editingFactor ? 'Editar Factor' : 'Nuevo Factor de Riesgo'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingFactor && (
              <div>
                <Label>ID (único, sin espacios)</Label>
                <Input
                  value={factorForm.id}
                  onChange={(e) => setFactorForm({ ...factorForm, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  placeholder="ej: operacion_sospechosa"
                  disabled={!!editingFactor}
                />
              </div>
            )}
            
            <div>
              <Label>Nombre del Factor</Label>
              <Input
                value={factorForm.nombre}
                onChange={(e) => setFactorForm({ ...factorForm, nombre: e.target.value })}
                placeholder="ej: Operación sospechosa detectada"
              />
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={factorForm.descripcion}
                onChange={(e) => setFactorForm({ ...factorForm, descripcion: e.target.value })}
                placeholder="Descripción detallada del factor de riesgo..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría</Label>
                <Select
                  value={factorForm.categoria}
                  onValueChange={(v) => setFactorForm({ ...factorForm, categoria: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.categorias?.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Puntos Base</Label>
                <Input
                  type="number"
                  value={factorForm.puntos_base}
                  onChange={(e) => setFactorForm({ ...factorForm, puntos_base: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={100}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={factorForm.activo}
                onCheckedChange={(checked) => setFactorForm({ ...factorForm, activo: checked })}
              />
              <Label>Factor activo</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFactorDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveFactor} 
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {saving ? 'Guardando...' : 'Guardar Factor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
